<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, useSlots, watch } from 'vue';

import { useTheme } from '../composables/useTheme';
import { HrCard, HrSkeleton, HrTable, HrTd, HrTr } from '../primitives';

/**
 * Data table — a table with toolbar slots and per-row action column injection.
 *
 * It sits directly on the surface it is given, with the header held apart by its own surface role
 * and a divider rather than by an enclosing edge. Pass `amongPeers` for the one case that earns a
 * card: a table that is a section of a screen rather than the screen's content.
 *
 * Forwards the body- and header-cell slots documented below. It does not forward every Quasar
 * table slot.
 *
 * **`row-actions` holds one overflow menu, not a row of icons.** An icon beside the menu running
 * what a menu entry already runs is two controls for one action, and the icons are unlabelled in
 * a column that is already the narrowest on screen. Inside the menu, the first entry is whatever
 * the row click does and the destructive one is last: see HrMenuItem for the ordering rule.
 *
 * All Quasar QTable props (columns, rows, rowKey, loading, …) are forwarded via `$attrs`.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Displaying tabular data with optional toolbar actions, bulk actions, or per-row menus
 * @slot actions Toolbar left — search fields, filter chips, or other table-level controls.
 * @slot bulk-actions Toolbar right — bulk actions, or a table-level control such as a column picker.
 * @slot row-actions Per-row action buttons (e.g. HrActionMenu); receives `{ row }`. One overflow menu — see above.
 * @slot body-cell-[column] Custom cell renderer; receives Quasar row slot props `{ row, col, value, ... }`.
 * @slot header-cell-[column] Custom header cell for the named column; receives Quasar's header slot props.
 * @slot header-selection Whole select-all control in the header; receives Quasar's header slot props.
 *   Taking it over costs the header its indeterminate state — pass it only when a row may be unselectable.
 * @slot body-selection Whole select control for one row; receives Quasar's row slot props.
 * @slot body Whole body row — nested or grouped rows. Owning it drops the cells, `row-actions` and `body-cell-*`.
 * @slot header Whole header row. Owning it drops every `header-cell-*` slot.
 */
defineOptions({ name: 'HrDataTable', inheritAttrs: false });
/*
 * inheritAttrs is off because attrs are spread onto the inner table explicitly, so automatic
 * fallthrough would apply them to the root as well. That is not merely redundant when the root is
 * a card: HrCard declares selected, clickable and disabled as booleans, so a table's selected rows
 * collide with the card's selected state.
 */

const props = defineProps({
  /**
   * The table is one section among peers on the screen — a recent-sessions table beside other
   * blocks on a client detail page — rather than the screen's content.
   *
   * A card in M3 is a bounded object: something that sits among peers, can be acted on as a whole,
   * and could be moved or dismissed. A table that IS the page is none of those, so the card would
   * assert a relationship that does not exist. It costs something too — the card edge is a heavier
   * line than the row hairlines and competes with them, its rounded corners fight a sticky header,
   * horizontal overflow has to clip against a rounded edge, and at narrow widths the inset eats
   * width the table needs more than anything else on the screen. So the caller states the
   * relationship and the surface follows, rather than the other way round.
   */
  amongPeers: {
    type: Boolean,
    default: false,
  },
  /**
   * The rows in hand are not the ones about to be shown, so hold the place with placeholders even
   * though there are records to draw. A saved view still arriving is the case: what is on screen is
   * the unfiltered set the view is about to replace, and letting it stand means the reader reads a
   * set that then changes under them.
   */
  pending: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const attrs = useAttrs();
const { hasTheme } = useTheme();

const hasRowActions = computed(() => !!slots['row-actions'] && !slots.body);

/*
 * The trailing column is the controls column, not the row-actions column. It holds row actions when
 * there are any and whatever the caller puts in its header either way — a column picker belongs
 * above the columns it governs, and a table with nothing to do to a row still has columns to pick.
 */
const hasControlsColumn = computed(
  () => (hasRowActions.value || !!slots['header-cell-actions']) && !slots.body,
);

/*
 * With inheritAttrs off, the DOM attributes have to be routed by hand, and they belong on the
 * card rather than the table: the card is this component's root, so it is the element a call
 * site's scoped style is qualified against and the one an e2e selector expects to find. On the
 * inner table a caller's class would match nothing. Everything else is a QTable prop or listener.
 */
const isCardAttr = (key) => key === 'class' || key === 'style'
  || key.startsWith('aria-') || key.startsWith('data-');

/* class and style are bound alongside the root's own, so only the rest are spread. */
const rootAttrs = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([key]) => isCardAttr(key) && key !== 'class' && key !== 'style'),
));

const tableAttrs = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([key]) => !isCardAttr(key)),
));

/*
 * Enough rows to read as a list rather than as one thing still arriving. Not a guess at how many
 * records there are, which is not knowable yet; it is how much page to hold still.
 */
const PENDING_ROW_COUNT = 6;

/*
 * A first load, which is the one with nothing on screen to keep, or a caller saying the rows in
 * hand are about to be replaced. A plain reload holds the records the reader is already reading and
 * lets the loading bar say more are coming: replacing a populated table with placeholders takes
 * away what they were looking at in order to say it is refreshing.
 */
const isFirstLoad = computed(
  () => props.pending || (!!attrs.loading && (attrs.rows ?? []).length === 0),
);

/*
 * Keyed on whatever the caller identifies rows by, because QTable tracks rows by that field and
 * placeholders that all answer `undefined` to it are one row as far as it is concerned.
 */
const pendingRows = computed(() => {
  const rowKey = attrs['row-key'] ?? attrs.rowKey ?? 'id';
  return Array.from({ length: PENDING_ROW_COUNT }, (unused, index) => ({
    [rowKey]: `pending-row-${index}`,
  }));
});

const effectiveAttrs = computed(() => {
  /*
   * Selection goes with the records. The placeholders are not rows anybody can act on, and QTable's
   * own select-all would otherwise hand a caller six synthetic objects as if they were records.
   */
  const withRows = isFirstLoad.value
    ? { ...tableAttrs.value, rows: pendingRows.value, selection: 'none' }
    : tableAttrs.value;

  if (!hasControlsColumn.value) return withRows;

  /*
   * Centered rather than right-aligned: the header holds a small icon button and the rows hold an
   * overflow menu, and the two are not always the same size. Aligned to the right edge, a larger
   * control puts its icon further from that edge than the smaller one above it, so the column
   * reads as two staggered lines. Centered, both sit on the column's axis whatever their size.
   */
  const actionsColumn = {
    name: 'actions',
    label: '',
    align: 'center',
    sortable: false,
    field: 'actions',
    classes: 'hr-data-table__actions-cell',
    headerClasses: 'hr-data-table__actions-cell',
  };

  return { ...withRows, columns: [...(attrs.columns ?? []), actionsColumn] };
});

const columns = computed(() => attrs.columns ?? []);

/*
 * The sticky actions column only reads as standing over the table once something is passing beneath
 * it. CSS cannot see a scroll offset, so the offset is read from Quasar's scroller and the cue is
 * bound to that. A ResizeObserver is on it too: widening the window past the point of overflow
 * leaves scrollLeft at zero without ever firing a scroll event, and the cue would otherwise stay.
 */
const rootRef = ref(null);
const isScrolledX = ref(false);
let scroller = null;
let resizeObserver = null;

// RTL scrolls the other way, and browsers disagree on the sign.
const readScroll = () => {
  isScrolledX.value = Math.abs(scroller?.scrollLeft ?? 0) > 0;
};

const releaseScroller = () => {
  scroller?.removeEventListener('scroll', readScroll);
  resizeObserver?.disconnect();
  scroller = null;
  resizeObserver = null;
};

const bindScroller = () => {
  const root = rootRef.value?.$el ?? rootRef.value;
  const next = root?.querySelector?.('.q-table__middle') ?? null;
  if (next === scroller) return;

  releaseScroller();
  scroller = next;
  if (!scroller) return;

  scroller.addEventListener('scroll', readScroll, { passive: true });
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(readScroll);
    resizeObserver.observe(scroller);
  }
  readScroll();
};

onMounted(async () => {
  await nextTick();
  bindScroller();
});

/*
 * QTable renders the scroller from a different branch per rendering mode — a plain div normally, a
 * QVirtualScroll under virtual-scroll — so switching mode hands back a new element and leaves the
 * listener on a node that no longer scrolls. Rows changing keeps the same wrapper, which is why this
 * watches the mode rather than the data. Both spellings are read because attrs carry whichever the
 * call site wrote.
 */
watch(
  () => [attrs.grid, attrs.virtualScroll ?? attrs['virtual-scroll']],
  async () => {
    await nextTick();
    bindScroller();
  },
);

onBeforeUnmount(releaseScroller);

/*
 * The header families run over the columns the table actually renders, the injected actions column
 * included: that column names nothing, so its header is the natural home for a control governing
 * the columns beside it, and a caller has no other way to reach a cell this component added.
 *
 * The body families stay on the caller's own columns, because `body-cell-actions` is already bound
 * above to render `row-actions`, and binding the same slot twice is an error.
 */
const headerColumns = computed(() => effectiveAttrs.value.columns ?? []);

/*
 * Without a card there is no card to render: M3 has three card variants and "no surface at all" is
 * not one of them, because it is the absence of a card rather than a kind of one.
 */
const rootProps = computed(() => (props.amongPeers
  // @todo Remove legacy-props when Legacy theme is removed
  ? { ...rootAttrs.value, shape: 'xl', legacyProps: { flat: true, bordered: true } }
  : rootAttrs.value));
</script>
<template>
  <component
    :is="amongPeers ? HrCard : 'div'"
    ref="rootRef"
    v-bind="rootProps"
    :class="[
      hasTheme ? 'hr-data-table' : null,
      // Which surface the table is on, not which palette paints it, so it holds in either theme.
      amongPeers && 'hr-data-table--among-peers',
      // @todo Remove when the Legacy theme is removed
      !hasTheme && 'hr-data-table--legacy',
      // Structure, not palette: the actions column holds still in either theme.
      'hr-data-table--actions-pinned',
      isScrolledX && 'hr-data-table--scrolled-x',
      $attrs.class,
    ]"
    :style="$attrs.style"
  >
    <hr-table
      v-bind="effectiveAttrs"
    >
      <template
        v-if="$slots.actions"
        #top-left="slotProps"
      >
        <div class="hr-data-table__slot-row">
          <slot
            name="actions"
            v-bind="slotProps ?? {}"
          />
        </div>
      </template>
      <template
        v-if="$slots['bulk-actions']"
        #top-right="slotProps"
      >
        <slot
          name="bulk-actions"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="hasRowActions"
        #body-cell-actions="slotProps"
      >
        <q-td
          :props="slotProps"
          class="hr-data-table__actions-cell"
          @click.stop
        >
          <slot
            name="row-actions"
            :row="slotProps.row"
          />
        </q-td>
      </template>
      <template
        v-for="col in columns.filter(c => $slots[`body-cell-${c.name}`])"
        :key="`body-cell-${col.name}`"
        #[`body-cell-${col.name}`]="slotProps"
      >
        <slot
          :name="`body-cell-${col.name}`"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-for="col in headerColumns.filter(c => $slots[`header-cell-${c.name}`])"
        :key="`header-cell-${col.name}`"
        #[`header-cell-${col.name}`]="slotProps"
      >
        <slot
          :name="`header-cell-${col.name}`"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots['header-selection']"
        #header-selection="slotProps"
      >
        <slot
          name="header-selection"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots['body-selection']"
        #body-selection="slotProps"
      >
        <slot
          name="body-selection"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots.header"
        #header="slotProps"
      >
        <slot
          name="header"
          v-bind="slotProps ?? {}"
        />
      </template>
      <!--
        The rows stay in the table rather than the table being swapped for a block: the column
        headers are known before the records are, and taking them away turns "these are loading"
        into "something is loading". Ahead of a caller's own body renderer, which reads fields a
        placeholder has none of.
      -->
      <template
        v-if="isFirstLoad"
        #body="slotProps"
      >
        <hr-tr :props="slotProps">
          <!--
            The column travels in the scope rather than on the key. QTd looks its column up by the
            vnode's key, and the key here lands on HrTd rather than on the QTd inside it, so the
            lookup finds nothing and the cell renders nothing at all.
          -->
          <hr-td
            v-for="col in slotProps.cols"
            :key="col.name"
            :props="{ ...slotProps, col }"
          >
            <hr-skeleton type="text" />
          </hr-td>
        </hr-tr>
      </template>
      <template
        v-else-if="$slots.body"
        #body="slotProps"
      >
        <slot
          name="body"
          v-bind="slotProps ?? {}"
        />
      </template>
    </hr-table>
  </component>
</template>
<style lang="scss" scoped>
.hr-data-table__slot-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/*
 * The table's container reaches the edges of whatever it is given — it is the content, not an
 * object sitting on the content — but its cells still inset, by the same amount a chip or a search
 * bar insets its own content. What lines up down the left of a screen is the leading edge of the
 * content in each row, not the edges of the containers holding it.
 */
.hr-data-table {
  --hr-data-table-edge: var(--app-comp-data-table-content-inset);
  /*
   * Whatever surface the table was actually put on. The sticky column has to paint an opaque one of
   * its own — a transparent sticky cell shows the rows sliding under it — so it has to be told, and
   * assuming the app surface made it a visibly different colour from its own rows inside anything
   * that paints its own, a dialog most obviously. Any enclosing card publishes this; outside one
   * there is nothing between the table and the page.
   */
  --hr-data-table-surface: var(--hr-card-surface, var(--app-surface));
}

// A card of its own, so the inset grows to clear the xl (28px) corner radius.
.hr-data-table--among-peers {
  --hr-data-table-edge: var(--app-comp-data-table-card-content-inset);
  --hr-data-table-surface: var(--hr-card-surface, var(--app-surface-container-low));
}

/*
 * The header is set apart by its own surface role and a divider beneath it rather than by an
 * enclosing edge, and it sticks. Both follow from dropping the card: a card border is a heavier
 * line than the row hairlines and would compete with them, and its rounded corners would have to
 * clip a stuck header. So neither applies to a table that kept its card — there the edge is
 * already doing the separating, and the radius is the thing a stuck header would fight.
 */
.hr-data-table:not(.hr-data-table--among-peers) :deep(.q-table thead th) {
  position: sticky;
  top: 0;
  z-index: var(--app-z-table-sticky-header);
  background: var(--app-surface-container);
  border-bottom: 1px solid var(--app-outline-variant);
}

/*
 * A sticky header covers the top of its own scrollport, so a row the keyboard scrolls into view
 * arrives underneath it. The margin is what the browser leaves clear when it does the scrolling.
 *
 * It costs nothing while the header is not actually sticking: the header only sticks once the
 * scrollport is bounded, and nothing is scrolled into view in a scrollport that does not scroll.
 */
.hr-data-table :deep(.q-table tbody tr) {
  scroll-margin-top: var(--app-comp-data-table-header-height);
}

// Separated by a divider rather than enclosed, for the same reason as the header.
.hr-data-table:not(.hr-data-table--among-peers) :deep(.q-table__bottom) {
  border-top: 1px solid var(--app-outline-variant);
}

.hr-data-table :deep(.q-table__top),
.hr-data-table :deep(.q-table__bottom) {
  padding-inline: var(--hr-data-table-edge);
}

.hr-data-table :deep(.q-table th:first-child),
.hr-data-table :deep(.q-table td:first-child) {
  padding-inline-start: var(--hr-data-table-edge);
}

.hr-data-table :deep(.q-table th:last-child),
.hr-data-table :deep(.q-table td:last-child) {
  padding-inline-end: var(--hr-data-table-edge);
}

/*
 * Full bleed on a phone. The page's padding is a large share of the little width there is, and the
 * header band, row dividers and horizontal scroller all read better running to the screen edges
 * than sitting inset inside a narrow column.
 *
 * The cells take that padding back on top of their own inset, so the first column still lines up
 * with the filter chips and the search above it even though the table around them does not.
 *
 * A table among peers is not the thing being bled to the edge — whatever holds it is.
 */
@media (max-width: 599px) {
  .hr-data-table:not(.hr-data-table--among-peers) {
    --hr-data-table-edge: calc(
      var(--hr-page-padding, var(--app-comp-page-padding-sm))
      + var(--app-comp-data-table-content-inset)
    );

    margin-inline: calc(-1 * var(--hr-page-padding, var(--app-comp-page-padding-sm)));
  }
}

// ── Selection checkboxes ─────────────────────────────────────────────
//
// QTable renders these itself, so they never pass through HrCheckbox and keep Quasar's own
// colors: grey-8 unchecked, which is all but black on a dark theme. The roles below are the ones
// HrCheckbox picks, applied here rather than by rendering HrCheckbox through the selection slots,
// which would cost the header its indeterminate state.
.hr-data-table :deep(.q-checkbox__inner) {
  color: var(--app-on-surface-variant);
}

.hr-data-table :deep(.q-checkbox__inner--truthy),
.hr-data-table :deep(.q-checkbox__inner--indet) {
  color: var(--app-primary);
}

.hr-data-table :deep(.q-checkbox__inner--truthy .q-checkbox__bg),
.hr-data-table :deep(.q-checkbox__inner--indet .q-checkbox__bg) {
  background: var(--app-primary);
  border-color: transparent;
}

// ── Sticky actions column ────────────────────────────────────────────
//
// HrTable resets the table background to transparent, so `inherit` resolves
// to transparent — content scrolls through and Quasar's hover tint composites
// against whatever is behind the cell (looks black on dark themes).
// We must use an explicit opaque background.
/*
 * Hung off a class applied in either theme, rather than the themed one. A column that stops holding
 * still when the palette changes is a different table, not a differently colored one. The surface
 * falls back for the same reason: outside the theme the variable is never set.
 */
/*
 * The 1px width shrinks the column to its content. QTable stretches to the full width and the auto
 * layout hands the spare width to every column, so on a table with one or two narrow columns the
 * controls column grew wide and its centered menu sat mid-row rather than at the trailing edge.
 */
.hr-data-table--actions-pinned :deep(.hr-data-table__actions-cell) {
  width: 1px;
  white-space: nowrap;
  position: sticky;
  right: 0;
  z-index: var(--app-z-table-sticky-cell);
  background: var(--hr-data-table-surface, var(--app-surface));
  transition: box-shadow 150ms ease;
}

/*
 * Only once the table has actually scrolled. A permanent edge says the column is floating over
 * something even when the whole table is in view and nothing is passing beneath it.
 */
.hr-data-table--scrolled-x :deep(.hr-data-table__actions-cell) {
  box-shadow: -4px 0 6px -4px color-mix(in srgb, var(--app-shadow) 30%, transparent);
}

/*
 * The header's actions cell is stuck on both axes, so it has to clear the rest of the header as
 * well as the body cells sliding under it, and it carries the header's surface rather than the
 * table's.
 */
.hr-data-table--actions-pinned :deep(th.hr-data-table__actions-cell) {
  z-index: var(--app-z-table-sticky-corner);
  background: var(--app-surface-container);
}

/*
 * On a card the header carries no surface of its own — the card is the surface, and the rule above
 * that sets one is guarded against this case. This cell is not, so it painted the header's role
 * over a header that was not wearing it and came out a lighter block in the corner.
 */
.hr-data-table--among-peers.hr-data-table--actions-pinned
  :deep(th.hr-data-table__actions-cell) {
  background: var(--hr-data-table-surface);
}

/*
 * The legacy palette maps every surface-container role to white, so the header band above lands
 * invisible there and the column titles float over the rows they label. surface-variant is the one
 * grey that palette defines. Only the fill is restated: the divider beneath already carries
 * outline-variant in either theme, and the header's stickiness is left as legacy has it.
 * @todo Remove when the Legacy theme is removed
 */
.hr-data-table--legacy:not(.hr-data-table--among-peers) :deep(.q-table thead th) {
  background: var(--app-surface-variant);
}

.hr-data-table--legacy :deep(th.hr-data-table__actions-cell) {
  background: var(--app-surface-variant);
}

</style>
